import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';
import { catchError, from, map, Observable, of } from 'rxjs';
import { Account, DataType, ExtractionRule, ExtractionRun } from './model';
import { generateDexieSchema, TableNames } from './utils/dexie.utils';

@Injectable({
  providedIn: 'root',
})
export class DexieService extends Dexie {
  public static readonly DATABASE_NAME = 'foundation-shell';

  constructor() {
    super(DexieService.DATABASE_NAME);
    this.version(1).stores({
      [TableNames.CURRENT_USER]: generateDexieSchema<Account>('id'),
      [TableNames.DOCUMENT]: generateDexieSchema<Document>('id'),
      [TableNames.DOCUMENT_TYPE]: generateDexieSchema<DocumentType>('id'),
      [TableNames.DATA_TYPE]: generateDexieSchema<DataType>('id'),
      [TableNames.EXTRACTION_RULE]: generateDexieSchema<ExtractionRule>('id'),
      [TableNames.EXTRACTION_RUN]: generateDexieSchema<ExtractionRun>('id'),
    });
  }

  /**
   * Get all items from a table.
   *
   * @param tableName - Table name.
   * @returns - Observable of all items in the table.
   */
  allItems<T>(tableName: string): Observable<T[]> {
    const table = this.getTable<T>(tableName);
    return from(table.toArray());
  }

  /**
   *  Get an item by id from a table.
   *
   * @param tableName - Table name.
   * @param id - Id of the item.
   * @returns - Observable of the item.
   */
  itemById<T>(tableName: string, id: string): Observable<T | undefined> {
    const table = this.getTable<T>(tableName);
    return from(table.get(id));
  }

  add<T>(tableName: string, item: T): Observable<T> {
    const table = this.getTable<T>(tableName);
    return from(table.add(item)).pipe(
      map(() => item),
      catchError(() => of(item))
    );
  }

  /**
   * Get only item from a table.
   *
   * @param tableName - Table name.
   * @returns - Observable of the item.
   */
  onlyItem<T>(tableName: string): Observable<T | undefined> {
    const table = this.getTable<T>(tableName);
    return from(table.toCollection().first());
  }

  /**
   * Get an item by attribute from a table.
   *
   * @param tableName - Table name.
   * @param attribute - Attribute to filter by.
   * @param value - Value of the attribute.
   * @returns - Observable of the item.
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  itemByAttribute<T>(tableName: string, attribute: keyof T, value: any): Observable<T | undefined> {
    const table = this.getTable<T>(tableName);
    return from(
      table
        .where(attribute as string)
        .equals(value)
        .first()
    );
  }

  /**
   * Get items by attribute from a table.
   *
   * @param tableName - Table name.
   * @param attribute - Attribute to filter by.
   * @param value - Value of the attribute.
   * @returns - Observable of items array.
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  itemsByAttribute<T>(tableName: string, attribute: keyof T, value: any): Observable<T[]> {
    const table = this.getTable<T>(tableName);
    return from(
      table
        .where(attribute as string)
        .equals(value)
        .toArray()
    );
  }

  /**
   * Delete an item by id from a table.
   *
   * @param tableName - Table name.
   * @param id - Id of the item.
   * @returns - Observable of void.
   */
  deleteItem(tableName: string, id: string): Observable<void> {
    const table = this.getTable<any>(tableName);
    return from(table.delete(id));
  }

  /**
   * Add or update multiple items in a table.
   *
   * @param tableName - Table name.
   * @param items - Array of items to add or update.
   * @returns - Observable of the keys of the added or updated items.
   */
  addOrUpdateItems<T>(tableName: string, items: T[]): Observable<string> {
    const table = this.getTable<T>(tableName);
    return from(table.bulkPut(items));
  }

  /**
   * Delete all items from table and perform bulk put.
   * @param tableName - Table name.
   * @param data - Array of items to update.
   * @returns - Observable of the keys of the added or updated items.
   *
   */
  updateTable<T>(tableName: string, data: T[]): Observable<T[]> {
    const table = this.getTable<T>(tableName);
    return from(
      this.transaction('rw', table, async () => {
        await table.clear();
        await table.bulkPut(data);
        return data;
      })
    );
  }

  /**
   * Clear all tables.
   * @returns - Observable of void.
   */
  clearAllTables(): Observable<void> {
    return from(
      this.transaction('rw', this.tables, async () => {
        for (const table of this.tables) {
          await table.clear();
        }
      })
    );
  }

  private getTable<T>(tableName: string): Table<T, string> {
    return this.table(tableName);
  }
}
